/**
 * Payment List Page Component
 * Displays paginated list of payments with filters and summary
 *
 * @author SAUBHAGYA Development Team
 * @since Story 4-5
 *
 * @description
 * Features:
 * - Summary cards (Total, Paid, In Progress, Failed)
 * - Filters (Status, Gaushala search, Date range)
 * - Data table with payment list
 * - Click to view details in modal
 * - Pagination controls
 * - Loading and empty states
 *
 * @example
 * ```tsx
 * import { PaymentListPage } from '@/pages/manufacturing/PaymentListPage';
 *
 * <PaymentListPage />
 * ```
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Calendar,
  CheckCircle2,
  CreditCard,
  TrendingUp,
  Clock,
  XCircle,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

import { usePayments, usePaymentSummary, PaymentFilters } from '@/hooks/usePayments';
import { searchGaushalaIds } from '@/services/manufacturing/manufacturingApi';
import { PaymentStatusBadge } from '@/components/manufacturing/badges/PaymentStatusBadge';
import { PaymentDetailModal } from '@/components/manufacturing/modals/PaymentDetailModal';
import { API_ENDPOINTS } from '@/lib/apiConfig';

/**
 * Format currency amount
 */
function formatCurrency(amount: number, currency: string = 'INR'): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date time
 */
function formatDateTime(dateString: string): string {
  try {
    return format(new Date(dateString), 'PP p');
  } catch {
    return dateString;
  }
}

/**
 * Format date only
 */
function formatDate(dateString: string): string {
  try {
    return format(new Date(dateString), 'PP');
  } catch {
    return dateString;
  }
}

/**
 * Fetch gaushala name from gaushala-service API
 */
function useGaushalaName(gaushalaId: number | null) {
  return useQuery<string>({
    queryKey: ['gaushala-name', gaushalaId],
    queryFn: async () => {
      if (!gaushalaId) return 'Unknown';
      try {
        const response = await fetch(
          API_ENDPOINTS.gaushala.getGaushalaName(gaushalaId)
        );
        if (!response.ok) return `Gaushala ${gaushalaId}`;
        const data = await response.json();
        return data.data || `Gaushala ${gaushalaId}`;
      } catch (error) {
        console.warn(`Failed to fetch gaushala name for ID ${gaushalaId}:`, error);
        return `Gaushala ${gaushalaId}`;
      }
    },
    enabled: !!gaushalaId,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

/**
 * Gaushala Name Display Component
 */
interface GaushalaNameCellProps {
  gaushalaId: number;
}

function GaushalaNameCell({ gaushalaId }: GaushalaNameCellProps) {
  const { data: gaushalaName, isLoading } = useGaushalaName(gaushalaId);

  if (isLoading) {
    return <Skeleton className="h-4 w-24" />;
  }

  return <span>{gaushalaName}</span>;
}

/**
 * Summary Card Component
 */
interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description?: string;
  isLoading?: boolean;
}

function SummaryCard({ title, value, icon, description, isLoading }: SummaryCardProps) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium truncate">{title}</CardTitle>
        <div className="shrink-0 ml-2">{icon}</div>
      </CardHeader>
      <CardContent className="overflow-hidden">
        {isLoading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <>
            <div className="text-xl sm:text-2xl font-bold truncate" title={String(value)}>{value}</div>
            {description && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{description}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Table Loading Skeleton
 */
function TableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <TableRow key={i}>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

/**
 * Payment List Page Component
 */
export function PaymentListPage() {
  // Get query parameters for auto-open functionality
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenTransactionId = searchParams.get('transactionId');
  const shouldAutoOpen = searchParams.get('autoOpen') === 'true';
  const statusFromUrl = searchParams.get('status');
  const validStatuses = ['COMPLETED', 'PENDING', 'FAILED'];
  const sanitizedStatus = statusFromUrl && validStatuses.includes(statusFromUrl) ? statusFromUrl : undefined;

  // State for filters
  const [filters, setFilters] = useState<PaymentFilters>({
    status: sanitizedStatus,
    gaushalaSearch: '',
    from: undefined,
    to: undefined,
    page: 0,
    size: 10,
  });

  // Debounced gaushala search → resolve IDs on frontend (bypasses broken backend-to-backend call)
  const [resolvedGaushalaIds, setResolvedGaushalaIds] = useState<number[] | undefined>(undefined);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    const searchTerm = (filters.gaushalaSearch || '').trim();
    if (!searchTerm) {
      setResolvedGaushalaIds(undefined);
      return;
    }
    debounceTimerRef.current = setTimeout(() => {
      searchGaushalaIds(searchTerm).then((ids) => {
        setResolvedGaushalaIds(ids.length > 0 ? ids : [-1]); // -1 = no match found, will return 0 results
      });
    }, 400);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters.gaushalaSearch]);

  // Memoized API filters - sends gaushalaIds instead of gaushalaSearch
  const apiFilters = useMemo(() => ({
    status: filters.status || undefined,
    gaushalaIds: resolvedGaushalaIds,
    from: filters.from || undefined,
    to: filters.to || undefined,
    page: filters.page,
    size: filters.size,
  }), [filters.status, resolvedGaushalaIds, filters.from, filters.to, filters.page, filters.size]);

  // State for selected payment
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);

  // Fetch data - use memoized apiFilters instead of raw filters
  const { data: paymentsData, isLoading: isLoadingPayments } = usePayments(apiFilters);
  const { data: summary, isLoading: isLoadingSummary } = usePaymentSummary();

  // Handle auto-open when transaction ID is provided via query params
  useEffect(() => {
    if (
      autoOpenTransactionId &&
      shouldAutoOpen &&
      !isLoadingPayments &&
      paymentsData
    ) {
      // Find payment with matching transaction ID
      const matchingPayment = paymentsData.content.find(
        (payment) => payment.transactionId === parseInt(autoOpenTransactionId, 10)
      );

      if (matchingPayment) {
        // Open the payment detail modal
        setSelectedPaymentId(matchingPayment.id);

        // Clear query parameters from URL
        setSearchParams({});
      }
    }
  }, [
    autoOpenTransactionId,
    shouldAutoOpen,
    paymentsData,
    isLoadingPayments,
    setSearchParams,
  ]);

  // Handle filter changes
  function handleStatusChange(value: string) {
    setFilters((prev) => ({
      ...prev,
      status: value === 'ALL' ? undefined : value,
      page: 0, // Reset to first page
    }));
  }

  function handleGaushalaSearchChange(value: string) {
    setFilters((prev) => ({
      ...prev,
      gaushalaSearch: value,
      page: 0,
    }));
  }

  function handleClearFilters() {
    setFilters({
      status: undefined,
      gaushalaSearch: '',
      from: undefined,
      to: undefined,
      page: 0,
      size: 10,
    });
  }

  // Pagination handlers
  function handlePreviousPage() {
    setFilters((prev) => ({
      ...prev,
      page: Math.max(0, (prev.page || 0) - 1),
    }));
  }

  function handleNextPage() {
    if (paymentsData && (filters.page || 0) < paymentsData.totalPages - 1) {
      setFilters((prev) => ({
        ...prev,
        page: (prev.page || 0) + 1,
      }));
    }
  }

  // Calculate summary values
  const totalPayments = (summary?.completedCount || 0) + (summary?.pendingCount || 0) + (summary?.processingCount || 0) + (summary?.failedCount || 0);
  const totalPaid = summary?.totalCompletedAmount || 0;
  const completed = summary?.completedCount || 0;
  const inProgress = (summary?.pendingCount || 0) + (summary?.processingCount || 0);
  const failed = summary?.failedCount || 0;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payments</h1>
          <p className="text-muted-foreground">
            View and manage payment transactions
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <SummaryCard
          title="Total Payments"
          value={totalPayments}
          icon={<CreditCard className="h-4 w-4 text-muted-foreground" />}
          description="All time"
          isLoading={isLoadingSummary}
        />
        <SummaryCard
          title="Amount Paid"
          value={formatCurrency(totalPaid)}
          icon={<TrendingUp className="h-4 w-4 text-green-600" />}
          description="Total completed amount"
          isLoading={isLoadingSummary}
        />
        <SummaryCard
          title="Completed"
          value={completed}
          icon={<CheckCircle2 className="h-4 w-4 text-green-600" />}
          description="Successfully processed"
          isLoading={isLoadingSummary}
        />
        <SummaryCard
          title="In Progress"
          value={inProgress}
          icon={<Clock className="h-4 w-4 text-blue-600" />}
          description="Pending & Processing"
          isLoading={isLoadingSummary}
        />
        <SummaryCard
          title="Failed"
          value={failed}
          icon={<XCircle className="h-4 w-4 text-red-600" />}
          description="Needs attention"
          isLoading={isLoadingSummary}
        />
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            {/* Status Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={filters.status || 'ALL'}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Gaushala Search */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Gaushala</label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search gaushala..."
                  value={filters.gaushalaSearch}
                  onChange={(e) => handleGaushalaSearchChange(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            {/* Date From */}
            <div className="space-y-2">
              <label className="text-sm font-medium">From Date</label>
              <Input
                type="date"
                value={filters.from || ''}
                max={filters.to || ''}
                onChange={(e) => {
                  const newFrom = e.target.value;
                  setFilters((prev) => ({
                    ...prev,
                    from: newFrom,
                    to: prev.to && newFrom > prev.to ? newFrom : prev.to,
                    page: 0,
                  }));
                }}
              />
            </div>

            {/* Date To */}
            <div className="space-y-2">
              <label className="text-sm font-medium">To Date</label>
              <Input
                type="date"
                value={filters.to || ''}
                min={filters.from || ''}
                onChange={(e) => {
                  const newTo = e.target.value;
                  setFilters((prev) => ({
                    ...prev,
                    to: newTo,
                    from: prev.from && newTo < prev.from ? newTo : prev.from,
                    page: 0,
                  }));
                }}
              />
            </div>
          </div>

          {/* Date range error */}
          {filters.from && filters.to && filters.from > filters.to && (
            <p className="mt-2 text-sm text-destructive">
              "From Date" cannot be later than "To Date".
            </p>
          )}

          {/* Clear Filters */}
          <div className="mt-4">
            <Button variant="outline" size="sm" onClick={handleClearFilters}>
              <X className="h-4 w-4 mr-2" />
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Payment List
            {paymentsData && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({paymentsData.totalElements} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch Code</TableHead>
                  <TableHead>Gaushala</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Gateway</TableHead>
                  <TableHead>Initiated</TableHead>
                  <TableHead>Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingPayments && <TableSkeleton />}

                {!isLoadingPayments && paymentsData?.content.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <CreditCard className="h-8 w-8 mb-2" />
                        <p>No payments found</p>
                        <p className="text-sm">Try adjusting your filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {!isLoadingPayments &&
                  paymentsData?.content.map((payment) => (
                    <TableRow
                      key={payment.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedPaymentId(payment.id)}
                    >
                      <TableCell className="font-medium">{payment.batchCode}</TableCell>
                      <TableCell><GaushalaNameCell gaushalaId={payment.gaushalaId} /></TableCell>
                      <TableCell>
                        {formatCurrency(payment.amount, payment.currency)}
                      </TableCell>
                      <TableCell>
                        <PaymentStatusBadge status={payment.status} size="sm" />
                      </TableCell>
                      <TableCell>{payment.gatewayType}</TableCell>
                      <TableCell className="text-sm">
                        {formatDateTime(payment.initiatedAt)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {payment.completedAt ? formatDate(payment.completedAt) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {paymentsData && paymentsData.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Page {(filters.page || 0) + 1} of {paymentsData.totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={(filters.page || 0) === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={(filters.page || 0) >= paymentsData.totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Detail Modal */}
      <PaymentDetailModal
        paymentId={selectedPaymentId}
        open={selectedPaymentId !== null}
        onOpenChange={(open) => !open && setSelectedPaymentId(null)}
      />
    </div>
  );
}

export default PaymentListPage;
